import type { DrawingTaskNote, TaskActivityLogEntry } from "./drawing-state";
import { sha256Hex } from "./entity-render-digest";

/**
 * Follower updates for Comments and Markings, found by diffing the previous
 * and next document (ADR 0036). Pure: collab calls it inside the store
 * transaction.
 */

export type ItemKind = "comment" | "marking";

export type UpdateKind =
	| "reply"
	| "state"
	| "assignee"
	| "responsibleTeam"
	| "subject"
	| "priority"
	| "dueDate";

/** The fields of a Comment or Marking (details) that the diff reads. */
export interface ItemSnapshot {
	id: string;
	userId: string;
	notes?: readonly Pick<DrawingTaskNote, "id" | "userId" | "timestamp">[];
	activityLog?: readonly TaskActivityLogEntry[];
	assignedTo?: string | null;
	responsibleTeamId?: string | null;
	notifyUserIds?: readonly string[] | null;
}

export interface ItemUpdate {
	itemId: string;
	kind: UpdateKind;
	actorId: string;
	/** Client time of the change; display only. */
	at: number;
	/** Stable across retries; unique per item together with the recipient. */
	sourceKey: string;
}

const UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const FIELD_UPDATE_KINDS: Readonly<Record<string, UpdateKind>> = {
	state: "state",
	assignedTo: "assignee",
	subject: "subject",
	title: "subject",
	priority: "priority",
	dueDate: "dueDate",
	team: "responsibleTeam",
};

const textEncoder = new TextEncoder();

const fingerprint = (entry: TaskActivityLogEntry): string =>
	JSON.stringify([entry.f, entry.u, entry.t, entry.o ?? null, entry.v ?? null]);

/**
 * Updates between two versions of a document. A reply is a note id found in no
 * item of `prev`; a field change is an allowlisted activity entry whose
 * fingerprint appears in no item of `prev`. Checking the whole document keeps
 * notes and history moved by merges from counting as new. Items missing from
 * `prev` (new items, merge results) and every item of a first store
 * (`prev === null`) give nothing: creation notifies nobody.
 */
export const diffItemUpdates = (
	prev: readonly ItemSnapshot[] | null,
	next: readonly ItemSnapshot[],
): ItemUpdate[] => {
	if (prev === null) {
		return [];
	}

	const prevItemIds = new Set<string>();
	const prevNoteIds = new Set<string>();
	const prevFingerprints = new Set<string>();
	for (const item of prev) {
		prevItemIds.add(item.id);
		for (const note of item.notes ?? []) {
			prevNoteIds.add(note.id);
		}
		for (const entry of item.activityLog ?? []) {
			prevFingerprints.add(fingerprint(entry));
		}
	}

	const updates: ItemUpdate[] = [];
	for (const item of next) {
		if (!prevItemIds.has(item.id)) {
			continue;
		}
		const seen = new Set<string>();

		for (const note of item.notes ?? []) {
			const sourceKey = `reply:${note.id}`;
			if (prevNoteIds.has(note.id) || seen.has(sourceKey)) {
				continue;
			}
			seen.add(sourceKey);
			updates.push({
				itemId: item.id,
				kind: "reply",
				actorId: note.userId,
				at: note.timestamp,
				sourceKey,
			});
		}

		for (const entry of item.activityLog ?? []) {
			const kind = FIELD_UPDATE_KINDS[entry.f];
			if (!kind || typeof entry.u !== "string") {
				continue;
			}
			const print = fingerprint(entry);
			if (prevFingerprints.has(print)) {
				continue;
			}
			// Hashed: the fingerprint carries old/new subject text, which can
			// outgrow a unique-index entry.
			const sourceKey = `field:${sha256Hex([textEncoder.encode(print)])}`;
			if (seen.has(sourceKey)) {
				continue;
			}
			seen.add(sourceKey);
			updates.push({
				itemId: item.id,
				kind,
				actorId: entry.u,
				at: entry.t,
				sourceKey,
			});
		}
	}
	return updates;
};

/**
 * Followers of an item: creator, repliers, assignee, Responsible-team members
 * and (Comments only) the notify list. Ids that aren't UUIDs are dropped; the
 * caller still checks that the Users exist.
 */
export const resolveFollowers = (
	kind: ItemKind,
	item: ItemSnapshot,
	teamMembers: ReadonlyMap<string, readonly string[]>,
): Set<string> => {
	const candidates: unknown[] = [
		item.userId,
		item.assignedTo,
		...(item.notes ?? []).map((note) => note.userId),
		...(item.responsibleTeamId
			? (teamMembers.get(item.responsibleTeamId) ?? [])
			: []),
		...(kind === "comment" ? (item.notifyUserIds ?? []) : []),
	];
	return new Set(
		candidates.filter(
			(id): id is string => typeof id === "string" && UUID_RE.test(id),
		),
	);
};
